import type { ReactNode } from "react"

/*
 * The rules behind the Filter menu and its chips, kept apart from the components so they can be
 * checked without a browser.
 */

/**
 * Whether the Filter menu offers a facet. One every row has a value of narrows nothing with fewer
 * than two values, so it isn't offered. One that `narrowsWithOne`, as a Label only some rows carry
 * does, is offered once it has a value.
 */
export const isOffered = ({
  values,
  narrowsWithOne = false,
}: {
  values: readonly unknown[]
  narrowsWithOne?: boolean
}) => values.length >= (narrowsWithOne ? 1 : 2)

/** How a chip joins its field to its value: "is" for one value, "is any of" for several. */
export function operatorOf(mode: "single" | "multi", chosen: number) {
  return mode === "multi" && chosen > 1 ? "is any of" : "is"
}

/**
 * The values a chip names, in the order they were chosen. A chosen value no row has any more, as
 * from an old link, is named by `labelOf` when the facet can name any value of its own, and
 * otherwise by its id with a capital, so the chip still says what is filtering the list and reads
 * "Access is Read" rather than the URL's "read".
 */
export function chipValuesOf(
  values: readonly { id: string; label: string; glyph?: ReactNode }[],
  chosen: readonly string[],
  labelOf: (id: string) => string = capitalised,
) {
  return chosen.map((id) => {
    const value = values.find((each) => each.id === id)
    return value ? { label: value.label, glyph: value.glyph } : { label: labelOf(id) }
  })
}

const capitalised = (id: string) => id.charAt(0).toUpperCase() + id.slice(1)
